<template>
  <RevCard class="relative p-24">
    <div class="flex flex-col justify-between">
      <div class="mb-12 flex items-center justify-between">
        <div class="flex items-center gap-12">
          <div class="bg-static-success-low rounded-md p-8">
            <IconSwap size="24" />
          </div>
          <div class="flex flex-col">
            <p class="body-1-bold">{{ i18n(translations.swapSummaryTitle) }}</p>
            <p class="text-static-default-low body-2">{{ title }}</p>
          </div>
        </div>
        <RevContextualMenu
          class="z-10"
          list-class-names="mt-16"
          position="bottom-start"
        >
          <template #trigger="{ show }">
            <RevButtonIcon
              data-test="swap-summary-show-options"
              :icon="IconMoreVertical"
              variant="secondary"
              @click="show"
            />
          </template>
          <template #items>
            <RevListItemInteractive @click="openSummaryModal">
              <template #label>
                {{ i18n(translations.swapSummaryContextualMenuMoreInfo) }}
              </template>
            </RevListItemInteractive>
            <RevListItemInteractive
              class="text-static-danger-hi"
              data-test="swap-summary-delete"
              @click="$emit('delete-swap')"
            >
              <template #label>
                {{ i18n(translations.swapSummaryContextualMenuDelete) }}
              </template>
            </RevListItemInteractive>
          </template>
        </RevContextualMenu>
      </div>
      <div
        class="flex flex-col gap-16 md:flex-row md:items-center md:justify-between md:pl-52"
      >
        <p class="body-2-bold">
          {{ expirationDateLabel }}
        </p>
        <div
          class="text-static-success-hi flex flex-row items-baseline gap-2 md:flex-col md:gap-0"
        >
          <p class="heading-2">{{ i18n.price(price) }}</p>
          <p class="caption-bold">{{ i18n(translations.swapSummaryPrice) }}</p>
        </div>
      </div>
    </div>
  </RevCard>
  <SummaryCartModal :price :shipping-mode :total-price-after-buyback />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api'
import { BUYBACK_SHIPPING_MODE } from '@backmarket/http-api/src/api-specs-buyback/constants'
import type { GetOfferResponseLegacy } from '@backmarket/http-api/src/api-specs-buyback/customer/getOfferLegacy'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevContextualMenu } from '@ds/components/ContextualMenu'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { openModal } from '@ds/components/ModalBase'
import { IconMoreVertical } from '@ds/icons/IconMoreVertical'
import { IconSwap } from '@ds/icons/IconSwap'

import { MODAL_NAMES } from '~/scopes/buyback/constants'

import SummaryCartModal from './SummaryCartModal.vue'
import translations from './SwapSummaryCartV2.translations'

const EXPIRATION_DAYS = 21

const { shippingMode } = defineProps<{
  price: Price
  title: string
  shippingMode?: GetOfferResponseLegacy['defaultShippingModeId']
  totalPriceAfterBuyback?: Price
}>()
defineEmits<{
  (event: 'delete-swap'): void
}>()

const i18n = useI18n()
const { trackClick } = useTracking()

const openSummaryModal = () => {
  trackClick({
    zone: 'cart_swapBlock',
    name: 'swap_cart_modal',
  })
  openModal(MODAL_NAMES.SWAP_SUMMARY)
}

const getExpirationDate = () => {
  const today = new Date()
  const date = new Date(today)
  date.setDate(today.getDate() + EXPIRATION_DAYS)

  return i18n.date(date, {
    month: 'long',
    day: 'numeric',
  })
}

const expirationDateLabel = computed(() => {
  return shippingMode === BUYBACK_SHIPPING_MODE.NEW_KIT ||
    shippingMode === BUYBACK_SHIPPING_MODE.KIT
    ? i18n(translations.swapSummaryExpirationDateMailingKit, {
        date: getExpirationDate(),
      })
    : i18n(translations.swapSummaryExpirationDate, {
        date: getExpirationDate(),
      })
})
</script>
